import { fetchCallBootstrap } from '@domain/call/api';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CallBootstrap } from '@domain/call/types';
import { LiveKitMediaRoomController } from '@domain/realtime/livekit/LiveKitMediaRoom';
import { closeGroupCall, joinGroupCall, startGroupCall } from '@domain/realtime/livekit/api';
import { RealtimeSignalingClient } from '@domain/realtime/signaling/RealtimeSignalingClient';
import type {
  BoardPatch,
  BoardSnapshot,
  RealtimeConversationRef,
  RealtimeEnvelope,
  RealtimeInvitePayload,
  RealtimeSessionKind,
} from '@domain/realtime/types';

type StartParams = {
  kind: RealtimeSessionKind;
  conversation: RealtimeConversationRef;
  targetUserIds: number[];
};

export type IncomingRealtimeSession = {
  callId: string;
  sessionId: string;
  kind: RealtimeSessionKind;
  conversation: RealtimeConversationRef;
  fromUserId: number;
  fromName: string;
  aggregateVersion?: number;
};

export type ActiveRealtimeSession = {
  callId: string;
  sessionId: string;
  kind: RealtimeSessionKind;
  conversation: RealtimeConversationRef;
  hostUserId: number;
  memberIds: number[];
  state: 'connecting' | 'active';
  livekitRoomName?: string;
  attendanceLegId?: string;
  aggregateVersion?: number;
  fence?: number;
  mediaCapability?: string;
};

type UseRealtimeCollaborationParams = {
  apiBase: string;
  bootstrap: CallBootstrap | null;
  currentUserId: number;
  currentUserName: string;
  onNotice?: (message: string, tone?: 'info' | 'error') => void;
};

function createSessionId(kind: RealtimeSessionKind) {
  return `rt-${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

type GroupMediaKind = Extract<RealtimeSessionKind, 'group-audio' | 'group-video'>;

function isVideoKind(kind: RealtimeSessionKind): kind is 'group-video' {
  return kind === 'group-video';
}

function isAudioKind(kind: RealtimeSessionKind): kind is 'group-audio' {
  return kind === 'group-audio';
}

function isMediaKind(kind: RealtimeSessionKind): kind is GroupMediaKind {
  return isAudioKind(kind) || isVideoKind(kind);
}

function isWhiteboardKind(kind: RealtimeSessionKind) {
  return kind === 'direct-whiteboard' || kind === 'group-whiteboard';
}

export function useRealtimeCollaboration({
  apiBase,
  bootstrap,
  currentUserId,
  currentUserName,
  onNotice,
}: UseRealtimeCollaborationParams) {
  const clientRef = useRef<RealtimeSignalingClient | null>(null);
  const activeRef = useRef<ActiveRealtimeSession | null>(null);
  const onNoticeRef = useRef(onNotice);
  const localStreamRef = useRef<MediaStream | null>(null);
  const liveKitRoomRef = useRef<LiveKitMediaRoomController | null>(null);
  const boardPatchesRef = useRef<BoardPatch[]>([]);
  const startingSessionRef = useRef(false);

  const [connectionState, setConnectionState] = useState<'idle' | 'connecting' | 'connected' | 'error'>('idle');
  const [incomingSession, setIncomingSession] = useState<IncomingRealtimeSession | null>(null);
  const [activeSession, setActiveSession] = useState<ActiveRealtimeSession | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStreams, setRemoteStreams] = useState<Array<{ userId: number; stream: MediaStream }>>([]);
  const [remoteBoardPatch, setRemoteBoardPatch] = useState<{ patch: BoardPatch; nonce: number } | null>(null);
  const [remoteBoardSnapshot, setRemoteBoardSnapshot] = useState<{ snapshot: BoardSnapshot; nonce: number } | null>(null);

  useEffect(() => {
    onNoticeRef.current = onNotice;
  }, [onNotice]);

  const emit = useCallback(<T extends Record<string, unknown>>(
    event: string,
    session: ActiveRealtimeSession | IncomingRealtimeSession,
    toUserId: number,
    payload: T,
  ) => {
    clientRef.current?.send({
      protocolVersion: 1,
      type: 'realtime',
      action: event,
      commandId: crypto.randomUUID(),
      sessionId: session.sessionId,
      callId: session.callId,
      peerUserId: toUserId,
      payload: {
        ...payload,
        conversation: session.conversation,
      },
    });
  }, []);

  const closeMediaRoom = useCallback(() => {
    void liveKitRoomRef.current?.disconnect();
    liveKitRoomRef.current = null;
    setRemoteStreams([]);
  }, []);

  const stopLocalStream = useCallback(() => {
    localStreamRef.current?.getTracks().forEach((track) => track.stop());
    localStreamRef.current = null;
    setLocalStream(null);
  }, []);

  const cleanup = useCallback(() => {
    startingSessionRef.current = false;
    closeMediaRoom();
    stopLocalStream();
    boardPatchesRef.current = [];
    setRemoteBoardPatch(null);
    setRemoteBoardSnapshot(null);
    activeRef.current = null;
    setActiveSession(null);
    setIncomingSession(null);
  }, [closeMediaRoom, stopLocalStream]);

  const rememberBoardPatch = useCallback((patch: BoardPatch) => {
    if ('type' in patch && patch.type === 'clear') {
      boardPatchesRef.current = [];
      return;
    }
    if ('id' in patch) {
      boardPatchesRef.current = [
        ...boardPatchesRef.current.filter((item) => !('id' in item) || item.id !== patch.id),
        patch,
      ];
    }
  }, []);

  const sendBoardSnapshot = useCallback((session: ActiveRealtimeSession | IncomingRealtimeSession, toUserId: number) => {
    emit('rt.board.snapshot', session, toUserId, {
      patches: boardPatchesRef.current,
    });
  }, [emit]);

  const setMediaLocalStream = useCallback((stream: MediaStream | null) => {
    localStreamRef.current = stream;
    setLocalStream(stream);
  }, []);

  const joinLiveKitRoom = useCallback(async (session: ActiveRealtimeSession) => {
    if (!bootstrap || !isMediaKind(session.kind) || !session.mediaCapability) {
      return;
    }
    const mediaRoom = new LiveKitMediaRoomController({
      apiBase,
      bootstrap,
      currentUserName,
      onLocalStream: setMediaLocalStream,
      onRemoteStreams: setRemoteStreams,
    });
    liveKitRoomRef.current = mediaRoom;
    session.livekitRoomName = await mediaRoom.join({
      mediaCapability: session.mediaCapability,
      kind: session.kind,
    });
    session.state = 'active';
    activeRef.current = { ...session };
    setActiveSession({ ...session });
  }, [apiBase, bootstrap, currentUserName, setMediaLocalStream]);

  const handleRealtimeEvent = useCallback(async (event: RealtimeEnvelope) => {
    const actorUserId = event.actorUserId ?? 0;
    if (actorUserId <= 0 || actorUserId === currentUserId) {
      return;
    }

    if (event.action === 'rt.session.invite') {
      const payload = event.payload as RealtimeInvitePayload;
      const kind = payload.kind;
      if (!kind) {
        onNoticeRef.current?.('收到无法识别的实时协作邀请', 'error');
        return;
      }
      // Direct chats are keyed by the peer user ID locally, not by the
      // sender's chat-side conversationId. Normalize here before page match.
      const conversation = payload.conversation ?? {
        type: 'single',
        id: actorUserId,
        title: `用户 #${actorUserId}`,
      };
      setIncomingSession({
        callId: event.callId,
        sessionId: event.sessionId || event.callId,
        kind,
        conversation,
        fromUserId: actorUserId,
        fromName: payload.fromName,
        aggregateVersion: payload.aggregateVersion,
      });
      return;
    }

    if (event.action === 'rt.session.reject') {
      onNoticeRef.current?.('对方暂时无法加入协作', 'info');
      return;
    }

    if (event.action === 'rt.session.end') {
      cleanup();
      return;
    }

    if (event.action === 'rt.session.leave') {
      const session = activeRef.current;
      if (session?.callId === event.callId) {
        const next = { ...session, memberIds: session.memberIds.filter((id) => id !== actorUserId) };
        activeRef.current = next;
        setActiveSession(next);
      }
      return;
    }

    if (event.action === 'rt.session.accept') {
      if (activeRef.current?.sessionId === (event.sessionId || event.callId) && isMediaKind(activeRef.current.kind)) {
        setActiveSession((current) => current ? {
          ...current,
          memberIds: [...new Set([...current.memberIds, actorUserId])],
          state: 'active',
        } : current);
        activeRef.current = activeRef.current ? {
          ...activeRef.current,
          memberIds: [...new Set([...activeRef.current.memberIds, actorUserId])],
          state: 'active',
        } : activeRef.current;
      }
      if (activeRef.current?.sessionId === (event.sessionId || event.callId) && isWhiteboardKind(activeRef.current.kind)) {
        setActiveSession((current) => current ? {
          ...current,
          memberIds: [...new Set([...current.memberIds, actorUserId])],
          state: 'active',
        } : current);
        activeRef.current = activeRef.current ? {
          ...activeRef.current,
          memberIds: [...new Set([...activeRef.current.memberIds, actorUserId])],
          state: 'active',
        } : activeRef.current;
        sendBoardSnapshot(activeRef.current, actorUserId);
      }
      return;
    }

    if (event.action === 'rt.board.patch') {
      const session = activeRef.current;
      if (!session || session.sessionId !== (event.sessionId || event.callId) || !isWhiteboardKind(session.kind)) {
        return;
      }
      const payload = event.payload as { patch?: BoardPatch } & BoardPatch;
      const patch = payload.patch ?? payload;
      if (patch) {
        rememberBoardPatch(patch);
        setRemoteBoardPatch({ patch, nonce: Date.now() + Math.random() });
      }
      return;
    }

    if (event.action === 'rt.board.snapshot-request') {
      const session = activeRef.current;
      if (session?.sessionId === (event.sessionId || event.callId) && isWhiteboardKind(session.kind)) {
        sendBoardSnapshot(session, actorUserId);
      }
      return;
    }

    if (event.action === 'rt.board.snapshot') {
      const session = activeRef.current;
      if (!session || session.sessionId !== (event.sessionId || event.callId) || !isWhiteboardKind(session.kind)) {
        return;
      }
      const payload = event.payload as Partial<BoardSnapshot>;
      const patches = Array.isArray(payload.patches) ? payload.patches : [];
      boardPatchesRef.current = patches;
      setRemoteBoardSnapshot({
        snapshot: { patches },
        nonce: Date.now() + Math.random(),
      });
    }
  }, [cleanup, currentUserId, emit, rememberBoardPatch, sendBoardSnapshot]);

  useEffect(() => {
    if (!bootstrap) {
      return undefined;
    }
    setConnectionState('connecting');
    const client = new RealtimeSignalingClient(bootstrap, {
      onOpen: () => setConnectionState('connected'),
      onClose: () => setConnectionState('idle'),
      onError: () => setConnectionState('error'),
      onEvent: (event) => {
        void handleRealtimeEvent(event).catch((error) => {
          onNoticeRef.current?.(error instanceof Error ? error.message : String(error), 'error');
        });
      },
    }, () => fetchCallBootstrap(apiBase, bootstrap.deviceId));
    clientRef.current = client;
    client.connect();
    return () => {
      client.close();
      if (clientRef.current === client) {
        clientRef.current = null;
      }
    };
  }, [apiBase, bootstrap, handleRealtimeEvent]);

  const startSession = useCallback(async ({ kind, conversation, targetUserIds }: StartParams) => {
    if (!bootstrap) {
      onNoticeRef.current?.('实时协作服务还没有连接', 'error');
      return;
    }
    if (startingSessionRef.current || activeRef.current) {
      onNoticeRef.current?.('已有实时协作正在进行', 'info');
      return;
    }
    const targets = [...new Set(targetUserIds.filter((id) => id && id !== currentUserId))];
    if (targets.length === 0) {
      onNoticeRef.current?.('没有可邀请的成员', 'error');
      return;
    }
    if (isMediaKind(kind) && conversation.type !== 'group') {
      onNoticeRef.current?.('群组音视频只能从群聊发起', 'error');
      return;
    }
    startingSessionRef.current = true;
    let session: ActiveRealtimeSession;
    try {
      if (isMediaKind(kind)) {
      const admission = await startGroupCall(apiBase, {
        commandId: crypto.randomUUID(),
        groupId: conversation.id,
        mediaKind: kind === 'group-video' ? 'video' : 'audio',
        deviceId: bootstrap.deviceId,
      });
      session = {
        callId: admission.callId,
        sessionId: admission.sessionId,
        kind,
        conversation,
        hostUserId: currentUserId,
        memberIds: [currentUserId, ...targets],
        state: 'connecting',
        attendanceLegId: admission.attendanceLegId,
        aggregateVersion: admission.aggregateVersion,
        fence: admission.fence,
        mediaCapability: admission.mediaCapability,
      };
      } else {
        const sessionId = createSessionId(kind);
        session = {
        callId: sessionId,
        sessionId,
        kind,
        conversation,
        hostUserId: currentUserId,
        memberIds: [currentUserId, ...targets],
        state: 'active',
        };
      }
    } catch (error) {
      startingSessionRef.current = false;
      throw error;
    }

    activeRef.current = session;
    setActiveSession({ ...session });

    try {
      if (isWhiteboardKind(kind)) {
        boardPatchesRef.current = [];
        setRemoteBoardPatch(null);
        setRemoteBoardSnapshot(null);
      }
      targets.forEach((targetUserId) => {
        // Invite first so the callee UI appears immediately; LiveKit join can
        // still be negotiating media or permissions in parallel.
        emit('rt.session.invite', session, targetUserId, {
          kind,
          conversation,
          fromName: currentUserName,
          aggregateVersion: session.aggregateVersion,
        });
      });
      if (isMediaKind(kind)) {
        await joinLiveKitRoom(session);
      }
    } catch (error) {
      if (isMediaKind(session.kind) && session.attendanceLegId && session.fence) {
        await closeGroupCall(apiBase, 'end', {
          commandId: crypto.randomUUID(),
          callId: session.callId,
          sessionId: session.sessionId,
          attendanceLegId: session.attendanceLegId,
          fence: session.fence,
        }).catch(() => undefined);
      }
      if (activeRef.current?.sessionId === session.sessionId) {
        cleanup();
      }
      throw error;
    } finally {
      startingSessionRef.current = false;
    }
  }, [apiBase, bootstrap, cleanup, currentUserId, currentUserName, emit, joinLiveKitRoom]);

  const acceptIncoming = useCallback(async () => {
    const incoming = incomingSession;
    if (!incoming) {
      return;
    }
    let admission;
    if (isMediaKind(incoming.kind)) {
      if (!bootstrap) return;
      admission = await joinGroupCall(apiBase, {
        commandId: crypto.randomUUID(),
        callId: incoming.callId,
        sessionId: incoming.sessionId,
        deviceId: bootstrap.deviceId,
      });
    }
    const session: ActiveRealtimeSession = {
      callId: incoming.callId,
      sessionId: incoming.sessionId,
      kind: incoming.kind,
      conversation: incoming.conversation,
      hostUserId: incoming.fromUserId,
      memberIds: [incoming.fromUserId, currentUserId],
      state: isMediaKind(incoming.kind) ? 'connecting' : 'active',
      ...(admission ? {
        attendanceLegId: admission.attendanceLegId,
        aggregateVersion: admission.aggregateVersion,
        fence: admission.fence,
        mediaCapability: admission.mediaCapability,
      } : {}),
    };
    activeRef.current = session;
    setActiveSession(session);
    setIncomingSession(null);
    try {
      if (isMediaKind(incoming.kind)) {
        await joinLiveKitRoom(session);
      }
      emit('rt.session.accept', incoming, incoming.fromUserId, {
        ok: true,
        aggregateVersion: session.aggregateVersion,
      });
    } catch (error) {
      if (admission) {
        await closeGroupCall(apiBase, 'leave', {
          commandId: crypto.randomUUID(),
          callId: session.callId,
          sessionId: session.sessionId,
          attendanceLegId: admission.attendanceLegId,
          fence: admission.fence,
        }).catch(() => undefined);
      }
      cleanup();
      throw error;
    }
    if (isWhiteboardKind(incoming.kind)) {
      boardPatchesRef.current = [];
      setRemoteBoardPatch(null);
      setRemoteBoardSnapshot(null);
      emit('rt.board.snapshot-request', session, incoming.fromUserId, {
        reason: 'joined',
      });
    }
  }, [apiBase, bootstrap, cleanup, currentUserId, emit, incomingSession, joinLiveKitRoom]);

  const rejectIncoming = useCallback(() => {
    const incoming = incomingSession;
    if (!incoming) {
      return;
    }
    emit('rt.session.reject', incoming, incoming.fromUserId, { reason: 'rejected' });
    setIncomingSession(null);
  }, [emit, incomingSession]);

  const endSession = useCallback(async () => {
    const session = activeRef.current;
    if (!session) {
      cleanup();
      return;
    }
    const isHost = session.hostUserId === currentUserId;
    try {
      if (isMediaKind(session.kind) && session.attendanceLegId && session.fence) {
        await closeGroupCall(apiBase, isHost ? 'end' : 'leave', {
          commandId: crypto.randomUUID(),
          callId: session.callId,
          sessionId: session.sessionId,
          attendanceLegId: session.attendanceLegId,
          fence: session.fence,
        });
      }
      session.memberIds
        .filter((id) => id !== currentUserId)
        .forEach((targetUserId) => emit(
          isHost ? 'rt.session.end' : 'rt.session.leave',
          session,
          targetUserId,
          { reason: isHost ? 'ended' : 'left' },
        ));
    } finally {
      cleanup();
    }
  }, [apiBase, cleanup, currentUserId, emit]);

  const sendBoardPatch = useCallback((patch: BoardPatch) => {
    const session = activeRef.current;
    if (!session || !isWhiteboardKind(session.kind)) {
      return;
    }
    rememberBoardPatch(patch);
    session.memberIds
      .filter((id) => id !== currentUserId)
      .forEach((targetUserId) => emit('rt.board.patch', session, targetUserId, patch));
  }, [currentUserId, emit, rememberBoardPatch]);

  useEffect(() => cleanup, [cleanup]);

  return {
    connectionState,
    incomingSession,
    activeSession,
    localStream,
    remoteStreams,
    remoteBoardPatch,
    remoteBoardSnapshot,
    startSession,
    acceptIncoming,
    rejectIncoming,
    endSession,
    sendBoardPatch,
  };
}
